import { normalizePreparation, type Preparation } from "../prepare/model.js";

/**
 * The Atelier's project document (docs/STUDIO.md): one carousel of the « liste commentée »
 * pilot format. It holds texts, settings, the slide order and references to stored images
 * (sha256), never the images themselves. Defaults come from the generator the members used
 * so far (generateur-carrousel 2.1, « Opulancia » profile, slides made by « Générer depuis
 * un texte »), so a new carousel starts where the published ones did.
 */
export const STUDIO_FORMAT = "opulancia-studio/1";
/**
 * A document that carries a preparation (« Préparer un carrousel ») is written as /2, so an
 * older version of the app refuses it instead of silently dropping the preparation on save.
 * Both are read; a document without preparation stays /1.
 */
export const STUDIO_FORMAT_PREPARED = "opulancia-studio/2";

export type SlideKind = "cover" | "item" | "end";
export type Objective = "follow" | "share" | "save" | "question";
export type Align = "left" | "center" | "right";
export type Position = "top" | "center" | "bottom";
export type ImageType = "image/jpeg" | "image/png" | "image/webp";

export interface ImageRef {
  sha256: string;
  type: ImageType;
  width: number;
  height: number;
}

export interface Slide {
  id: string;
  kind: SlideKind;
  /** Title as typed; shown in capitals. A line break forces a new line. */
  title: string;
  /** Secondary text as typed (Inter 600). */
  sub: string;
  /** Highlighted words or expressions of the title. */
  accent: string[];
  /** « block »: black letters on an ochre rectangle (the generator's « surligner »); « letters »: ochre letters. */
  highlight: "block" | "letters";
  image: ImageRef | null;
  /** Crop: focus point in % of the image, zoom ≥ 1 (the generator's recadrage and zoom). */
  fx: number;
  fy: number;
  zoom: number;
  /** Uniform darkening of the image, 0 to 0.8 (« Assombrir l'image »). */
  darken: number;
  /** Bottom fade to black, 0 to 1 (« Dégradé de lisibilité »; 1 covers the lower half). */
  fade: number;
  /** How dark the fade gets, 0 to 1 (1: the generator's fade, black at the bottom). */
  fadeStrength: number;
  align: Align;
  position: Position;
  /** null: the template's size. */
  titleSize: number | null;
  subSize: number | null;
  /** Cover: circled arrow; item: small arrow. Never on the last slide. */
  arrow: boolean;
  /** Last slide only: what it asks for. */
  objective?: Objective;
  /** « save » objective: the button's label. */
  button?: string;
  /** « follow » objective: the account shown on the card. */
  handle?: string;
}

export interface StudioDoc {
  format: typeof STUDIO_FORMAT | typeof STUDIO_FORMAT_PREPARED;
  template: "liste";
  settings: {
    /** Progress bar on the list items (generator: on « Contenu » slides, from 2 of them). */
    progress: boolean;
    /** Numbered items: number in an ochre circle (generator: « Compte à rebours »). */
    numbered: boolean;
  };
  slides: Slide[];
  /** The assisted preparation this project comes from (references, sources, claims, briefs, validations). */
  preparation?: Preparation;
}

export const LIMITS = { slides: 20, title: 220, sub: 400, accentWords: 8, name: 120 } as const;

export const OBJECTIVES: Record<Objective, { label: string; title: string; sub: string; accent: string[] }> = {
  follow: { label: "S'abonner", title: "ON NE SE REVERRA PEUT-ÊTRE JAMAIS.", sub: "À MOINS QUE TU T’ABONNES MAINTENANT !", accent: ["JAMAIS"] },
  share: { label: "Partager", title: "PARTAGE CE POST À QUELQU'UN QUI DOIT LE VOIR", sub: "", accent: ["PARTAGE"] },
  save: { label: "Enregistrer", title: "GARDE-LE POUR PLUS TARD.", sub: "", accent: ["GARDE-LE"] },
  question: { label: "Commenter", title: "ET TOI, TU EN PENSES QUOI ?", sub: "Dis-le en commentaire.", accent: ["TOI"] },
};

export const DEFAULT_HANDLE = "opulanciamedia";
export const DEFAULT_BUTTON = "Enregistre →";

let counter = 0;
export const newId = () => `s${Date.now().toString(36)}${(counter++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function newSlide(kind: SlideKind, patch: Partial<Slide> = {}): Slide {
  const base: Slide = {
    id: newId(),
    kind,
    title: "",
    sub: "",
    accent: [],
    highlight: "block",
    image: null,
    fx: 50,
    fy: 50,
    zoom: 1,
    darken: kind === "cover" ? 0.35 : 0.4,
    fade: 0,
    fadeStrength: 1,
    align: "center",
    position: "bottom",
    titleSize: null,
    subSize: null,
    arrow: kind !== "end",
  };
  if (kind === "end") {
    const o = OBJECTIVES.follow;
    Object.assign(base, { objective: "follow", title: o.title, sub: o.sub, accent: [...o.accent], handle: DEFAULT_HANDLE, button: DEFAULT_BUTTON });
  }
  return { ...base, ...patch };
}

export function newDoc(): StudioDoc {
  return {
    format: STUDIO_FORMAT,
    template: "liste",
    settings: { progress: true, numbered: false },
    slides: [
      newSlide("cover", { title: "TITRE DE LA LISTE", accent: ["LISTE"] }),
      newSlide("item", { title: "PREMIER ÉLÉMENT", sub: "Une phrase d'explication.", accent: ["PREMIER"] }),
      newSlide("item", { title: "DEUXIÈME ÉLÉMENT", sub: "Une phrase d'explication.", accent: ["DEUXIÈME"] }),
      newSlide("end"),
    ],
  };
}

/** Sets the last slide's objective, with its default texts (one objective at a time). */
export function setObjective(slide: Slide, objective: Objective): void {
  const o = OBJECTIVES[objective];
  slide.objective = objective;
  slide.title = o.title;
  slide.sub = o.sub;
  slide.accent = [...o.accent];
  slide.handle ??= DEFAULT_HANDLE;
  slide.button ??= DEFAULT_BUTTON;
}

/**
 * The generator's « Générer depuis un texte »: the first line is the cover's title, then one
 * block per item separated by an empty line (first line = title, the next ones = explanation).
 * *word* marks a highlight; without one, the last word of the title is highlighted, as there.
 */
export function slidesFromText(text: string): { cover: Partial<Slide> | null; items: Array<Partial<Slide>> } {
  const blocks = text
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
  const parse = (raw: string) => {
    const stars = [...raw.matchAll(/\*([^*]+)\*/g)].map((m) => m[1]!.trim()).filter(Boolean);
    const title = raw.replace(/\*([^*]+)\*/g, "$1").trim();
    if (stars.length) return { title, accent: stars };
    const last = lastWord(title);
    return { title, accent: last ? [last] : [] };
  };
  let cover: Partial<Slide> | null = null;
  const items: Array<Partial<Slide>> = [];
  blocks.forEach((block, i) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    if (i === 0 && lines.length === 1) {
      cover = parse(lines[0]!);
      return;
    }
    const head = parse(lines[0] ?? "");
    items.push({ ...head, sub: lines.slice(1).join("\n") });
  });
  return { cover, items };
}

/** The title's last word, punctuation around it removed (« lieu » for « Troisième « lieu » »). */
const lastWord = (title: string) =>
  title
    .split(/\s+/)
    .map((w) => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ""))
    .filter(Boolean)
    .at(-1) ?? "";

/**
 * After the title changed: when none of the highlighted words is in it any more, the last
 * word is highlighted instead (the generator's default). An empty list stays empty.
 */
export function followTitle(slide: Slide): void {
  const title = slide.title.toLocaleUpperCase("fr-FR");
  if (!slide.accent.length || slide.accent.some((a) => title.includes(a.toLocaleUpperCase("fr-FR")))) return;
  const last = lastWord(slide.title);
  slide.accent = last ? [last] : [];
}

const clamp = (v: unknown, lo: number, hi: number, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback);
const str = (v: unknown, max: number) => (typeof v === "string" ? v.slice(0, max) : "");
const oneOf = <T extends string>(v: unknown, values: readonly T[], fallback: T): T => (values.includes(v as T) ? (v as T) : fallback);

/** A document read from the database or a project file, made safe to edit and render. */
export function normalizeDoc(input: unknown): StudioDoc {
  const raw = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  if (raw["format"] !== undefined && raw["format"] !== STUDIO_FORMAT && raw["format"] !== STUDIO_FORMAT_PREPARED) throw new Error(`Format de projet inconnu : ${String(raw["format"])}`);
  const preparation = normalizePreparation(raw["preparation"]);
  const settings = (raw["settings"] && typeof raw["settings"] === "object" ? raw["settings"] : {}) as Record<string, unknown>;
  const slidesIn = Array.isArray(raw["slides"]) ? raw["slides"].slice(0, LIMITS.slides) : [];
  const slides = slidesIn.map((value) => {
    const s = (value && typeof value === "object" ? value : {}) as Record<string, unknown>;
    const kind = oneOf(s["kind"], ["cover", "item", "end"] as const, "item");
    const img = s["image"] as Record<string, unknown> | null | undefined;
    const image: ImageRef | null =
      img && typeof img["sha256"] === "string" && /^[0-9a-f]{64}$/.test(img["sha256"])
        ? { sha256: img["sha256"], type: oneOf(img["type"], ["image/jpeg", "image/png", "image/webp"] as const, "image/jpeg"), width: clamp(img["width"], 1, 20000, 1080), height: clamp(img["height"], 1, 20000, 1350) }
        : null;
    const slide = newSlide(kind, {
      id: typeof s["id"] === "string" && s["id"] ? s["id"].slice(0, 40) : newId(),
      title: str(s["title"], LIMITS.title),
      sub: str(s["sub"], LIMITS.sub),
      accent: Array.isArray(s["accent"]) ? s["accent"].filter((w): w is string => typeof w === "string" && w.trim() !== "").slice(0, LIMITS.accentWords).map((w) => w.slice(0, 60)) : [],
      highlight: oneOf(s["highlight"], ["block", "letters"] as const, "block"),
      image,
      fx: clamp(s["fx"], 0, 100, 50),
      fy: clamp(s["fy"], 0, 100, 50),
      zoom: clamp(s["zoom"], 1, 3, 1),
      darken: clamp(s["darken"], 0, 0.8, kind === "cover" ? 0.35 : 0.4),
      fade: clamp(s["fade"], 0, 1, 0),
      fadeStrength: clamp(s["fadeStrength"], 0, 1, 1),
      align: oneOf(s["align"], ["left", "center", "right"] as const, "center"),
      position: oneOf(s["position"], ["top", "center", "bottom"] as const, "bottom"),
      titleSize: s["titleSize"] === null || s["titleSize"] === undefined ? null : clamp(s["titleSize"], 28, 220, 100),
      subSize: s["subSize"] === null || s["subSize"] === undefined ? null : clamp(s["subSize"], 20, 80, 35),
      arrow: kind === "end" ? false : typeof s["arrow"] === "boolean" ? s["arrow"] : true,
    });
    if (kind === "end") {
      slide.objective = oneOf(s["objective"], ["follow", "share", "save", "question"] as const, "follow");
      slide.button = str(s["button"], 40) || DEFAULT_BUTTON;
      slide.handle = str(s["handle"], 40).replace(/^@/, "") || DEFAULT_HANDLE;
    }
    return slide;
  });
  return {
    format: preparation ? STUDIO_FORMAT_PREPARED : STUDIO_FORMAT,
    template: "liste",
    settings: { progress: settings["progress"] !== false, numbered: settings["numbered"] === true },
    slides,
    ...(preparation ? { preparation } : {}),
  };
}

/** A new project that starts as a preparation: no slide until the scenario is imported and built. */
export function preparedDoc(preparation: Preparation): StudioDoc {
  return { format: STUDIO_FORMAT_PREPARED, template: "liste", settings: { progress: true, numbered: false }, slides: [], preparation };
}

/** Items of the list, in order, with their rank (the number shown when the list is numbered). */
export function itemRank(doc: StudioDoc, index: number): number {
  return doc.slides.slice(0, index + 1).filter((s) => s.kind === "item").length;
}

/**
 * The progress bar, as computed by the generator: only on « Contenu » slides — here the list
 * items when they are not numbered (numbered items were « Compte à rebours » slides there,
 * without the bar) — once there are at least two of them; width = rank among them / their count.
 */
export function progressOf(doc: StudioDoc, index: number): number | null {
  const slide = doc.slides[index];
  if (!doc.settings.progress || doc.settings.numbered || slide?.kind !== "item") return null;
  const total = doc.slides.filter((s) => s.kind === "item").length;
  if (total < 2) return null;
  return itemRank(doc, index) / total;
}

/** Checks a document can be exported: one cover first, one last slide, items between. */
export function structureProblems(doc: StudioDoc): string[] {
  const problems: string[] = [];
  const kinds = doc.slides.map((s) => s.kind);
  if (kinds[0] !== "cover") problems.push("La couverture doit être la première slide.");
  if (kinds.filter((k) => k === "cover").length !== 1) problems.push("Il faut une seule couverture.");
  if (kinds.at(-1) !== "end") problems.push("La slide de fin doit être la dernière.");
  if (kinds.filter((k) => k === "end").length > 1) problems.push("Une seule slide de fin.");
  if (!kinds.includes("item")) problems.push("Ajoute au moins un élément de liste.");
  doc.slides.forEach((s, i) => {
    if (!s.title.trim()) problems.push(`Slide ${i + 1} : titre vide.`);
  });
  return problems;
}

/**
 * Image references used by the document, each stored once: the slides', then the cover concepts
 * compared during the preparation (the ones set aside included), so the project file and the
 * backup keep them.
 */
export const imageRefs = (doc: StudioDoc): ImageRef[] => {
  const refs = [...doc.slides.map((s) => s.image), ...(doc.preparation?.cover_study?.concepts ?? []).map((c) => c.image)].filter((r): r is ImageRef => !!r);
  return [...new Map(refs.map((r) => [r.sha256, r])).values()];
};
